import React, { useState, useEffect } from 'react';
import DynamicSidebar from '../../../components/ui/DynamicSidebar';
import DashboardHeader from '../../../components/ui/DashboardHeader';
import Icon from '../../../components/AppIcon';
import { apiService } from '../../../services/api';

const ChartOfAccountsPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ account_name: '', account_type: '', is_postable: true });
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService?.get('/portal/financials/accounts');
      const data = response?.data;
      if (data?.status_code === 200) {
        setAccounts(data?.details || []);
      } else {
        setError(data?.status_description || 'Failed to load accounts');
      }
    } catch (err) {
      setError(err?.response?.data?.status_description || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountTypes = async () => {
    try {
      const response = await apiService?.get('/portal/financials/account-types');
      const data = response?.data;
      if (data?.status_code === 200) {
        setAccountTypes(data?.details || []);
      }
    } catch (err) {
      // silently fail for dropdown
    }
  };

  useEffect(() => {
    fetchAccounts();
    fetchAccountTypes();
  }, []);

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0.00';
    return Number(value)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleOpenModal = () => {
    setFormData({ account_name: '', account_type: '', is_postable: true });
    setFormError(null);
    setFormSuccess(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormError(null);
    setFormSuccess(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData?.account_name?.trim()) {
      setFormError('Account name is required');
      return;
    }
    if (!formData?.account_type) {
      setFormError('Account type is required');
      return;
    }
    try {
      setSubmitting(true);
      setFormError(null);
      const response = await apiService?.post('/portal/financials/accounts', {
        account_name: formData?.account_name,
        account_type: formData?.account_type,
        is_postable: formData?.is_postable,
      });
      const data = response?.data;
      if (data?.status_code === 200 || data?.status_code === 201) {
        setFormSuccess('Account created successfully');
        await fetchAccounts();
        setTimeout(() => {
          handleCloseModal();
        }, 1500);
      } else {
        setFormError(data?.status_description || 'Failed to create account');
      }
    } catch (err) {
      setFormError(err?.response?.data?.status_description || 'Failed to create account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(prev => !prev)} />
      <div className="flex">
        <DynamicSidebar
          isCollapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(prev => !prev)}
        />
        <main
          className={`flex-1 transition-all duration-300 ${
            sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
          } mt-16 p-6`}
        >
          {/* Breadcrumb */}
          <div className="mb-6">
            <div className="flex items-center space-x-2 text-sm text-text-secondary mb-1">
              <span>Finance</span>
              <Icon name="ChevronRight" size={14} />
              <span>Setup</span>
              <Icon name="ChevronRight" size={14} />
              <span className="text-text-primary font-medium">Chart of Accounts</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">Chart of Accounts</h1>
                <p className="text-text-secondary text-sm mt-1">Manage your organization's chart of accounts</p>
              </div>
              <button
                onClick={handleOpenModal}
                className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
              >
                <Icon name="Plus" size={16} />
                <span>Create Account</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-surface border border-border rounded-lg overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-text-secondary">Loading accounts...</span>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Icon name="AlertCircle" size={40} className="text-error mb-3" />
                <p className="text-text-secondary">{error}</p>
                <button onClick={fetchAccounts} className="mt-4 text-primary text-sm hover:underline">Retry</button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-surface-secondary border-b border-border">
                      <th className="text-left px-4 py-3 text-text-secondary font-medium">Account Code</th>
                      <th className="text-left px-4 py-3 text-text-secondary font-medium">Account Name</th>
                      <th className="text-left px-4 py-3 text-text-secondary font-medium">Type</th>
                      <th className="text-right px-4 py-3 text-text-secondary font-medium">Total Debit</th>
                      <th className="text-right px-4 py-3 text-text-secondary font-medium">Total Credit</th>
                      <th className="text-right px-4 py-3 text-text-secondary font-medium">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accounts?.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-12 text-text-secondary">
                          <div className="flex flex-col items-center">
                            <Icon name="BookOpen" size={32} className="text-text-secondary mb-2" />
                            <span>No accounts found</span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      accounts?.map((account, index) => (
                        <tr key={account?.account_code || index} className="border-b border-border hover:bg-surface-secondary/50 transition-colors">
                          <td className="px-4 py-3 font-mono text-text-primary font-medium">{account?.account_code}</td>
                          <td className="px-4 py-3 text-text-primary">{account?.account_name}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                              {account?.type_code}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right text-text-primary">{formatNumber(account?.total_debit)}</td>
                          <td className="px-4 py-3 text-right text-text-primary">{formatNumber(account?.total_credit)}</td>
                          <td className="px-4 py-3 text-right font-medium">
                            <span className={Number(account?.balance) < 0 ? 'text-error' : 'text-success'}>
                              {formatNumber(account?.balance)}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
      {/* Create Account Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-surface rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-lg font-semibold text-text-primary">Create Account</h2>
              <button onClick={handleCloseModal} className="text-text-secondary hover:text-text-primary transition-colors">
                <Icon name="X" size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
              {formError && (
                <div className="flex items-center space-x-2 bg-error/10 border border-error/20 text-error rounded-lg px-3 py-2 text-sm">
                  <Icon name="AlertCircle" size={16} />
                  <span>{formError}</span>
                </div>
              )}
              {formSuccess && (
                <div className="flex items-center space-x-2 bg-success/10 border border-success/20 text-success rounded-lg px-3 py-2 text-sm">
                  <Icon name="CheckCircle" size={16} />
                  <span>{formSuccess}</span>
                </div>
              )}

              {/* Account Name */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Account Name <span className="text-error">*</span></label>
                <input
                  type="text"
                  value={formData?.account_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, account_name: e?.target?.value }))}
                  placeholder="Enter account name"
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                />
              </div>

              {/* Account Type */}
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">Account Type <span className="text-error">*</span></label>
                <select
                  value={formData?.account_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, account_type: e?.target?.value }))}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                >
                  <option value="">Select account type</option>
                  {accountTypes?.map((type) => (
                    <option key={type?.type_code} value={type?.type_code}>
                      {type?.type_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Is Postable */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-text-primary">Is Postable</label>
                  <p className="text-xs text-text-secondary mt-0.5">Allow transactions to be posted to this account</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_postable: !prev?.is_postable }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                    formData?.is_postable ? 'bg-primary' : 'bg-border'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData?.is_postable ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-sm text-text-secondary border border-border rounded-lg hover:bg-surface-secondary transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center space-x-2"
                >
                  {submitting && <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>}
                  <span>{submitting ? 'Creating...' : 'Create Account'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChartOfAccountsPage;
